import React from 'react';
import { AlertTriangle, ArrowDownLeft, ArrowUpRight, Scale, ListOrdered } from 'lucide-react';
import { fmtMoney, fmtNum, fmtFechaHora } from './formato';

const Stat = ({ icon: Icon, label, valor, sub, tono, testid }) => (
  <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-3">
    <div className="flex items-center gap-1.5 text-xs text-slate-500"><Icon className={`h-3.5 w-3.5 ${tono}`} />{label}</div>
    <div className={`text-lg font-semibold tabular-nums mt-0.5 ${tono}`} data-testid={testid}>{valor}</div>
    {sub && <div className="text-[11px] text-slate-400">{sub}</div>}
  </div>
);

const SENT = { INGRESO: 'Ingreso', EGRESO: 'Egreso', ENTRE_PROPIAS: 'Entre propias' };

const Celda = ({ m, k }) => {
  if (k === 'fecha') return <td className="px-3 py-1.5 whitespace-nowrap">{fmtFechaHora(m.fecha)}</td>;
  if (k === 'monto') return <td className="px-3 py-1.5 text-right tabular-nums whitespace-nowrap">{fmtMoney(m.monto)}</td>;
  if (k === 'sentido') {
    return <td className={`px-3 py-1.5 ${m.sentido === 'INGRESO' ? 'text-emerald-700' : m.sentido === 'EGRESO' ? 'text-red-700' : 'text-slate-500'}`}>{SENT[m.sentido]}</td>;
  }
  return <td className="px-3 py-1.5 max-w-[220px] truncate text-slate-600" title={m[k] ?? ''}>{m[k] ?? ''}</td>;
};

export const VistaPrevia = ({ prev, diseno, catalogo }) => {
  const labels = Object.fromEntries((catalogo || []).map((c) => [c.key, c.label]));
  const cols = (diseno?.columnas || []).filter((k) => labels[k]);
  return (
    <div className="space-y-3" data-testid="expmov-preview">
      {(prev.avisos || []).map((a) => (
        <div key={a} className="flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2.5" data-testid="expmov-preview-aviso">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />{a}
        </div>
      ))}
      {prev.supera_maximo && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-2.5">
          Son más de {fmtNum(prev.maximo)} movimientos: acotá el período o los filtros para poder exportar.
        </div>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat icon={ListOrdered} label="Movimientos" valor={fmtNum(prev.cantidad)} tono="text-slate-900 dark:text-white"
          sub={prev.primer_mov ? `${fmtFechaHora(prev.primer_mov)} a ${fmtFechaHora(prev.ultimo_mov)}` : 'sin movimientos'} testid="expmov-prev-cantidad" />
        <Stat icon={ArrowDownLeft} label="Ingresos" valor={fmtMoney(prev.total_ingresos)} sub={`${fmtNum(prev.cantidad_ingresos)} movimientos`} tono="text-emerald-700" testid="expmov-prev-ingresos" />
        <Stat icon={ArrowUpRight} label="Egresos" valor={fmtMoney(prev.total_egresos)} sub={`${fmtNum(prev.cantidad_egresos)} movimientos`} tono="text-red-700" testid="expmov-prev-egresos" />
        <Stat icon={Scale} label="Neto" valor={fmtMoney(prev.neto)} tono="text-blue-800"
          sub={prev.entre_cuentas_propias ? `${fmtNum(prev.entre_cuentas_propias)} entre cuentas propias (no suman)` : null} testid="expmov-prev-neto" />
      </div>
      {prev.primeras?.length > 0 && (
        <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-x-auto">
          <p className="text-xs text-slate-500 px-3 pt-2">Primeros {prev.primeras.length} movimientos del período, con las columnas que se exportan</p>
          <table className="w-full text-xs mt-1" data-testid="expmov-preview-tabla">
            <thead>
              <tr className="text-left text-slate-500 border-b border-slate-100 dark:border-slate-800">
                {cols.map((k) => <th key={k} className="px-3 py-1.5 font-medium whitespace-nowrap" data-testid={`expmov-preview-col-${k}`}>{labels[k]}</th>)}
              </tr>
            </thead>
            <tbody>
              {prev.primeras.map((m) => (
                <tr key={m.id_waya} className="border-b border-slate-50 dark:border-slate-800/50">
                  {cols.map((k) => <Celda key={k} m={m} k={k} />)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default VistaPrevia;
